import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Breadcrumb, Tabs, Tab } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaTrophy, FaShip, FaComment } from 'react-icons/fa';
import './Community.css';
import ThreadCard from './shared/ThreadCard';

export default function LakeDetail() {
  const { lakeSlug } = useParams();
  const [lakeData, setLakeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLake = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/lakes/${lakeSlug}`);
        const data = await res.json();
        
        if (data.success) {
          setLakeData({
            lake: data.lake,
            tournaments: data.tournaments || [],
            discussions: data.discussions || []
          });
        }
      } catch (error) {
        console.error("Error fetching lake details", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLake();
  }, [lakeSlug]);

  if (loading) {
    return (
      <div className="community-container d-flex justify-content-center align-items-center">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  if (!lakeData || !lakeData.lake) {
    return (
      <div className="community-container text-center py-5">
        <h2 className="text-white">Lake not found</h2>
        <Link to="/community/lakes" className="community-link mt-3 d-inline-block">Return to Lake Hub</Link>
      </div>
    );
  }

  const { lake, tournaments, discussions } = lakeData;

  return (
    <div className="community-container">
      {/* Hero Header */}
      <div 
        style={{
          background: `linear-gradient(to bottom, rgba(18, 18, 18, 0.4), #121212), url(${lake.heroImage || 'https://via.placeholder.com/1200x400?text=Lake'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '60px 0 30px',
          marginTop: '-40px',
          marginBottom: '40px'
        }}
      >
        <Container>
          <Breadcrumb className="mb-4">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/community" }} className="text-warning fw-bold text-shadow">
              Community Home
            </Breadcrumb.Item>
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/community/lakes" }} className="text-warning fw-bold text-shadow">
              Lakes
            </Breadcrumb.Item>
            <Breadcrumb.Item active className="text-white fw-bold text-shadow">{lake.name}</Breadcrumb.Item>
          </Breadcrumb>

          <h1 className="display-4 fw-bold text-white text-shadow mb-2">{lake.name}</h1>
          <div className="text-white h5 mb-3 text-shadow">
            <FaMapMarkerAlt className="me-2 text-warning" /> 
            {lake.state || 'United States'}
          </div>
          
          <div className="d-flex gap-3 mt-4">
            <Link to={`/community/new?lake=${lake.id}`} className="btn btn-warning fw-bold">
              <FaComment className="me-2" /> Start Discussion
            </Link>
          </div>
        </Container>
      </div>

      <Container>
        <Row>
          <Col lg={8}>
            <Tabs defaultActiveKey="discussions" className="mb-4 community-tabs border-bottom border-dark">
              
              {/* Discussions Tab */}
              <Tab eventKey="discussions" title="Recent Discussions">
                <div className="pt-3">
                  {discussions.length > 0 ? (
                    discussions.map(thread => (
                      <ThreadCard key={thread.id} thread={thread} />
                    ))
                  ) : (
                    <div className="community-card text-center py-5">
                      <h4 className="text-muted mb-3">No discussions for {lake.name} yet</h4>
                      <Link to={`/community/new?lake=${lake.id}`} className="btn btn-outline-warning">Be the first to post</Link>
                    </div>
                  )}
                </div>
              </Tab>

              {/* Tournaments Tab */}
              <Tab eventKey="tournaments" title="Tournaments">
                <div className="pt-3">
                  {tournaments.length > 0 ? (
                    tournaments.map(tournament => (
                      <div key={tournament.id} className="community-card d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                          <h4 className="text-white mb-1"><FaTrophy className="text-warning me-2" />{tournament.title}</h4>
                          <div className="text-muted small">
                            Organizer: {tournament.organizer} | Dates: {new Date(tournament.startDate).toLocaleDateString()} - {new Date(tournament.endDate).toLocaleDateString()}
                          </div>
                        </div>
                        {tournament.registrationUrl && (
                          <a href={tournament.registrationUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-warning">
                            Registration details
                          </a>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="community-card text-center py-5">
                      <h4 className="text-muted mb-3">No upcoming tournaments</h4>
                      <p className="text-muted mb-0">Check back later for updates on tournament schedules.</p>
                    </div>
                  )}
                </div>
              </Tab>
              
            </Tabs>
          </Col>

          <Col lg={4}>
            {/* Lake Info Sidebar */}
            <div className="community-card mb-4">
              <h4 className="text-white mb-3 border-bottom border-dark pb-2">About {lake.name}</h4>
              <p className="text-muted" style={{ lineHeight: '1.6' }}>
                {lake.description || 'No description available for this lake.'}
              </p>
            </div>

            {/* Marinas/Ramps */}
            <div className="community-card">
              <h4 className="text-white mb-3 border-bottom border-dark pb-2"><FaShip className="me-2 text-warning"/> Marinas & Ramps</h4>
              {lake.marinas && lake.marinas.length > 0 ? (
                <ul className="text-muted ps-3 mb-0">
                  {lake.marinas.map((marina, idx) => (
                    <li key={idx} className="mb-2">{marina}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted small mb-0">No marina information listed yet.</p>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
